import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { blogService } from '../services/blog/blogService';
import type { BlogContextType, BlogPost, BlogPostInput } from '../types';

const BlogContext = createContext<BlogContextType | null>(null);

export function BlogProvider({ children }: { children: ReactNode }) {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshPosts = useCallback(async (options?: { silent?: boolean; admin?: boolean }) => {
    if (!options?.silent) {
      setIsLoading(true);
    }
    setError(null);

    try {
      const nextPosts = options?.admin
        ? await blogService.fetchAllPosts()
        : await blogService.fetchPublishedPosts();
      setPosts(nextPosts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Blog yazıları yüklenemedi.');
    } finally {
      if (!options?.silent) {
        setIsLoading(false);
      }
    }
  }, []);

  const getPostById = useCallback(
    (id: number) => posts.find((post) => post.id === id),
    [posts],
  );

  const getPostBySlug = useCallback(
    (slug: string) => posts.find((post) => post.slug === slug),
    [posts],
  );

  const loadPost = useCallback(async (id: number): Promise<BlogPost | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const post = await blogService.getPost(id);
      setPosts((prev) => {
        if (prev.some((item) => item.id === id)) {
          return prev.map((item) => (item.id === id ? post : item));
        }
        return [...prev, post];
      });
      return post;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Blog yazısı yüklenemedi.');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadPostBySlug = useCallback(async (slug: string): Promise<BlogPost | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const post = await blogService.getPostBySlug(slug);
      setPosts((prev) => {
        if (prev.some((item) => item.id === post.id)) {
          return prev.map((item) => (item.id === post.id ? post : item));
        }
        return [...prev, post];
      });
      return post;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Blog yazısı yüklenemedi.');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createPost = useCallback(async (input: BlogPostInput) => {
    const post = await blogService.createPost(input);
    setPosts((prev) => [...prev, post]);
    return post;
  }, []);

  const updatePost = useCallback(async (id: number, input: BlogPostInput) => {
    const post = await blogService.updatePost(id, input);
    setPosts((prev) => prev.map((item) => (item.id === id ? post : item)));
    return post;
  }, []);

  const deletePost = useCallback(async (id: number) => {
    await blogService.deletePost(id);
    setPosts((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const value = useMemo(
    () => ({
      posts,
      isLoading,
      error,
      refreshPosts,
      loadPost,
      loadPostBySlug,
      getPostById,
      getPostBySlug,
      createPost,
      updatePost,
      deletePost,
    }),
    [
      posts,
      isLoading,
      error,
      refreshPosts,
      loadPost,
      loadPostBySlug,
      getPostById,
      getPostBySlug,
      createPost,
      updatePost,
      deletePost,
    ],
  );

  return <BlogContext.Provider value={value}>{children}</BlogContext.Provider>;
}

export function useBlog() {
  const context = useContext(BlogContext);
  if (!context) {
    throw new Error('useBlog must be used within BlogProvider');
  }
  return context;
}
